import React, { useMemo, useState } from 'react';
import { Box, Text, render, renderToString, useApp, useInput } from 'ink';
import { TextInput } from '@inkjs/ui';

export type InitPackageManager = 'npm' | 'pnpm' | 'yarn' | 'bun';
export type InitAuthMode = 'standardagents' | 'local';
export type InitProgressStage = 'project' | 'auth' | 'env' | 'scaffold';

const STAGE_LABELS: Record<InitProgressStage, string> = {
  project: 'Project',
  auth: 'Auth',
  env: 'Env',
  scaffold: 'Scaffold',
};

const STANDARDAGENTS_FLOW: InitProgressStage[] = ['project', 'auth', 'scaffold'];
const LOCAL_FLOW: InitProgressStage[] = ['project', 'auth', 'env', 'scaffold'];

export function getInitProgressStages(authMode: InitAuthMode = 'standardagents'): InitProgressStage[] {
  return authMode === 'local' ? LOCAL_FLOW : STANDARDAGENTS_FLOW;
}

export function getInitStepInfo(stage: InitProgressStage, authMode: InitAuthMode = 'standardagents'): {
  step: number;
  total: number;
  labels: string[];
} {
  const flow = getInitProgressStages(authMode);
  const stepIndex = flow.indexOf(stage);
  return {
    step: stepIndex === -1 ? 1 : stepIndex + 1,
    total: flow.length,
    labels: flow.map((item) => STAGE_LABELS[item]),
  };
}

export interface InitStepFrameState {
  stage: InitProgressStage;
  title: string;
  subtitle?: string;
  projectName?: string;
  packageManager?: string;
  authMode?: InitAuthMode;
}

export const AUTH_SIDE_BY_SIDE_MIN_COLUMNS = 110;

export function shouldUseSideBySideAuthCards(columns: number): boolean {
  return columns >= AUTH_SIDE_BY_SIDE_MIN_COLUMNS;
}

function clearInkScreen(stdout: NodeJS.WriteStream = process.stdout): void {
  stdout.write('\x1b[2J\x1b[H');
}

async function settleStdinBeforePrompt(stdin: NodeJS.ReadStream = process.stdin): Promise<void> {
  if (!stdin.isTTY) return;

  stdin.pause();
  while (stdin.read() !== null) {
    // Drain any buffered key events (for example Enter from previous screen)
  }
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  while (stdin.read() !== null) {
    // One more pass after microtask boundary
  }
}

interface SelectOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  bullets?: string[];
  badge?: string;
}

interface SelectPromptProps<T extends string> {
  title: string;
  subtitle?: string;
  footerHint?: string;
  options: SelectOption<T>[];
  defaultIndex?: number;
  horizontal?: boolean;
  density?: 'comfortable' | 'compact';
  onCancel?: () => void;
  onSubmit: (value: T) => void;
}

function Stepper({ activeStage, authMode }: { activeStage: InitProgressStage; authMode: InitAuthMode }) {
  const flow = getInitProgressStages(authMode);
  const activeIndex = flow.indexOf(activeStage);
  return (
    <Box flexDirection="row" marginBottom={1}>
      {flow.map((stage, index) => {
        const label = STAGE_LABELS[stage];
        const isActive = index === activeIndex;
        const isDone = index < activeIndex;
        const fg = isActive ? 'black' : isDone ? 'green' : 'gray';
        const bg = isActive ? 'cyan' : undefined;
        const marker = isDone ? '✓' : isActive ? '●' : '·';
        return (
          <Box key={stage} marginRight={1}>
            <Text color={fg} backgroundColor={bg}>
              {' '}
              {marker} {label}{' '}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

function InitStepFrame(props: InitStepFrameState) {
  const mode = props.authMode ?? 'standardagents';
  const { step, total } = getInitStepInfo(props.stage, mode);
  const metadata = useMemo(() => {
    const items: Array<{ id: 'project' | 'pm' | 'mode'; label: string; value: string }> = [];
    if (props.projectName) {
      items.push({ id: 'project', label: 'Project', value: props.projectName });
    }
    if (props.packageManager) {
      items.push({ id: 'pm', label: 'PM', value: props.packageManager });
    }
    if (props.authMode) {
      items.push({
        id: 'mode',
        label: 'Mode',
        value: props.authMode === 'standardagents' ? 'StandardAgents' : 'Local',
      });
    }
    return items;
  }, [props.authMode, props.packageManager, props.projectName]);

  return (
    <Box flexDirection="column" paddingX={1}>
      <Box borderStyle="round" borderColor="cyan" paddingX={1} flexDirection="column">
        <Text color="cyanBright" bold>
          StandardAgents CLI
        </Text>
        <Text color="gray">Init Wizard</Text>
      </Box>
      <Box marginTop={1}>
        <Stepper activeStage={props.stage} authMode={mode} />
      </Box>
      {metadata.length > 0 && (
        <Box marginBottom={1} flexDirection="row">
          {metadata.map((item, index) => {
            const valueColor =
              item.id === 'project'
                ? 'whiteBright'
                : item.id === 'pm'
                  ? 'magentaBright'
                  : item.value === 'StandardAgents'
                    ? 'greenBright'
                    : 'yellowBright';
            return (
              <Box key={item.id} flexDirection="row">
                <Text color="gray">{item.label} </Text>
                <Text color={valueColor}>{item.value}</Text>
                {index < metadata.length - 1 ? <Text color="gray">   •   </Text> : null}
              </Box>
            );
          })}
        </Box>
      )}
      <Text color="whiteBright" bold>
        Step {step}/{total}: {props.title}
      </Text>
      {props.subtitle ? <Text color="gray">{props.subtitle}</Text> : null}
    </Box>
  );
}

function OptionCard<T extends string>({
  option,
  selected,
  width,
}: {
  option: SelectOption<T>;
  selected: boolean;
  width: number;
}) {
  const borderColor = selected ? 'cyan' : 'gray';
  const titleColor = selected ? 'cyanBright' : 'whiteBright';
  const textColor = selected ? 'whiteBright' : 'gray';

  return (
    <Box
      width={width}
      borderStyle="round"
      borderColor={borderColor}
      flexDirection="column"
      paddingX={1}
      paddingY={0}
      marginRight={1}
    >
      <Box>
        <Text color={titleColor} bold>
          {option.label}
        </Text>
        {option.badge ? (
          <Text color={selected ? 'cyanBright' : 'gray'}>{`  ${option.badge}`}</Text>
        ) : null}
      </Box>
      {option.description ? (
        <Box marginTop={0}>
          <Text color={textColor}>{option.description}</Text>
        </Box>
      ) : null}
      {option.bullets?.map((line) => (
        <Text key={line} color={textColor}>
          {'• '}
          {line}
        </Text>
      ))}
    </Box>
  );
}

function SelectPrompt<T extends string>({
  title,
  subtitle,
  footerHint,
  options,
  defaultIndex = 0,
  horizontal = false,
  density = 'comfortable',
  onCancel,
  onSubmit,
}: SelectPromptProps<T>) {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const { exit } = useApp();
  const width = process.stdout.columns ?? 120;
  const cardWidth = horizontal ? Math.max(34, Math.floor((width - 8) / options.length)) : Math.max(42, width - 6);

  useInput((input, key) => {
    if (key.upArrow || input === 'k' || key.leftArrow || input === 'h') {
      setSelectedIndex((prev) => (prev - 1 + options.length) % options.length);
      return;
    }
    if (key.downArrow || input === 'j' || key.rightArrow || input === 'l') {
      setSelectedIndex((prev) => (prev + 1) % options.length);
      return;
    }
    if (key.return) {
      onSubmit(options[selectedIndex].value);
      exit();
      return;
    }
    if (key.escape) {
      onCancel?.();
      exit();
      return;
    }
    if (key.ctrl && input === 'c') {
      process.exit(1);
    }
  });

  return (
    <Box flexDirection="column" paddingX={1}>
      <Text color="whiteBright" bold>
        {title}
      </Text>
      {subtitle ? <Text color="gray">{subtitle}</Text> : null}
      {density === 'compact' ? (
        <Box marginTop={1} flexDirection="column">
          {options.map((option, index) => {
            const selected = index === selectedIndex;
            return (
              <Box key={option.value} marginBottom={0}>
                <Text color={selected ? 'black' : 'white'} backgroundColor={selected ? 'cyan' : undefined} bold={selected}>
                  {selected ? '›' : ' '} {option.label}
                  {option.badge ? ` ${option.badge}` : ''}
                </Text>
                {option.description ? <Text color={selected ? 'cyan' : 'gray'}>{`  ${option.description}`}</Text> : null}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box marginTop={1} flexDirection={horizontal ? 'row' : 'column'}>
          {options.map((option, index) => (
            <Box key={option.value} marginBottom={horizontal ? 0 : 1}>
              <OptionCard option={option} selected={index === selectedIndex} width={cardWidth} />
            </Box>
          ))}
        </Box>
      )}
      <Text color="gray">{footerHint ?? 'Use arrows (or h/j/k/l) and Enter to continue.'}</Text>
    </Box>
  );
}

async function runSelectPrompt<T extends string>(
  props: Omit<SelectPromptProps<T>, 'onSubmit'>,
): Promise<T | null> {
  let resolved: T | null = null;
  let cancelled = false;
  await settleStdinBeforePrompt();
  clearInkScreen();
  const instance = render(
    <SelectPrompt
      {...props}
      onCancel={() => {
        cancelled = true;
      }}
      onSubmit={(value) => {
        resolved = value;
      }}
    />,
    { exitOnCtrlC: false },
  );
  await instance.waitUntilExit();
  if (resolved !== null) return resolved;
  if (cancelled) return null;
  return null;
}

interface ProjectNamePromptProps {
  question: string;
  frame: InitStepFrameState;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}

function ProjectNamePrompt({ question, frame, onCancel, onSubmit }: ProjectNamePromptProps) {
  const { exit } = useApp();
  const label = question.trim().replace(/:$/, '');

  useInput((input, key) => {
    if (key.ctrl && input === 'c') {
      process.exit(1);
      return;
    }
    if (key.escape) {
      onCancel();
      exit();
      return;
    }
  });

  return (
    <Box flexDirection="column">
      <InitStepFrame {...frame} />
      <Box flexDirection="column" paddingX={1} marginTop={1}>
        <Box borderStyle="round" borderColor="cyan" paddingX={1} marginTop={1}>
          <Text color="gray">{label}: </Text>
          <TextInput
            placeholder="acme-agents"
            onSubmit={(nextValue) => {
              const trimmed = nextValue.trim();
              if (!trimmed) {
                return;
              }
              onSubmit(trimmed);
              exit();
            }}
          />
        </Box>
        <Text color="gray">Type to edit, Enter to continue.</Text>
      </Box>
    </Box>
  );
}

export async function promptProjectNameWithInk(
  question: string,
  frame: InitStepFrameState,
): Promise<string | null> {
  let resolved: string | null = null;
  let cancelled = false;
  await settleStdinBeforePrompt();
  clearInkScreen();
  const instance = render(
    <ProjectNamePrompt
      question={question}
      frame={frame}
      onCancel={() => {
        cancelled = true;
      }}
      onSubmit={(value) => {
        resolved = value;
      }}
    />,
    { exitOnCtrlC: false },
  );
  await instance.waitUntilExit();
  if (cancelled) return null;
  return resolved ?? '';
}

export interface LocalEnvInput {
  openrouterKey: string;
  openaiKey: string;
  adminPassword: string;
}

interface LocalEnvPromptProps {
  frame: Pick<InitStepFrameState, 'projectName' | 'packageManager' | 'authMode'>;
  onCancel: () => void;
  onSubmit: (value: LocalEnvInput) => void;
}

const LOCAL_ENV_FIELDS = [
  {
    id: 'openrouter',
    title: 'OpenRouter API key',
    description: 'Optional. Needed for OpenRouter models.',
    placeholder: 'sk-or-v1-... (optional)',
  },
  {
    id: 'openai',
    title: 'OpenAI API key',
    description: 'Optional. Needed for OpenAI models.',
    placeholder: 'sk-... (optional)',
  },
  {
    id: 'password',
    title: 'Temporary admin password',
    description: 'Press Enter to use default: admin',
    placeholder: 'admin',
  },
] as const;

type LocalEnvFieldId = (typeof LOCAL_ENV_FIELDS)[number]['id'];

function LocalEnvPrompt({ frame, onCancel, onSubmit }: LocalEnvPromptProps) {
  const { exit } = useApp();
  const [fieldIndex, setFieldIndex] = useState(0);
  const [draft, setDraft] = useState<Record<LocalEnvFieldId, string>>({
    openrouter: '',
    openai: '',
    password: '',
  });
  const activeField = LOCAL_ENV_FIELDS[fieldIndex];

  useInput((input, key) => {
    if (key.ctrl && input === 'c') {
      process.exit(1);
      return;
    }
    if (key.escape) {
      onCancel();
      exit();
      return;
    }
  });

  const maskFieldValue = (fieldId: LocalEnvFieldId, value: string): string => {
    if (!value) return '';
    if (fieldId === 'password') return '•'.repeat(value.length);
    return value;
  };

  return (
    <Box flexDirection="column">
      <InitStepFrame
        stage="env"
        title="Environment setup"
        subtitle="Add optional provider keys and local admin access."
        projectName={frame.projectName}
        packageManager={frame.packageManager}
        authMode={frame.authMode}
      />
      <Box marginTop={1} paddingX={1} flexDirection="column">
        {LOCAL_ENV_FIELDS.map((field, index) => {
          const isActive = index === fieldIndex;
          const isComplete = index < fieldIndex;
          const marker = isComplete ? '✓' : isActive ? '›' : '·';
          return (
            <Box key={field.id} flexDirection="column" marginBottom={1}>
              <Text color={isActive ? 'cyanBright' : isComplete ? 'greenBright' : 'gray'} bold={isActive}>
                {marker} {field.title}
              </Text>
              <Text color="gray">{field.description}</Text>
              {isActive ? (
                <Box borderStyle="round" borderColor="cyan" paddingX={1}>
                  <TextInput
                    key={field.id}
                    defaultValue={draft[field.id]}
                    placeholder={field.placeholder}
                    onSubmit={(nextValue) => {
                      const trimmed = nextValue.trim();
                      const nextDraft = { ...draft, [field.id]: trimmed };
                      setDraft(nextDraft);

                      if (fieldIndex < LOCAL_ENV_FIELDS.length - 1) {
                        setFieldIndex(fieldIndex + 1);
                        return;
                      }

                      onSubmit({
                        openrouterKey: nextDraft.openrouter,
                        openaiKey: nextDraft.openai,
                        adminPassword: nextDraft.password || 'admin',
                      });
                      exit();
                    }}
                  />
                </Box>
              ) : (
                <Text color={isComplete ? 'white' : 'gray'}>
                  {maskFieldValue(field.id, draft[field.id]) || '(not set)'}
                </Text>
              )}
            </Box>
          );
        })}
      </Box>
      <Box paddingX={1}>
        <Text color="gray">Press Enter to continue each field.</Text>
      </Box>
    </Box>
  );
}

export async function collectLocalEnvWithInk(
  frame: Pick<InitStepFrameState, 'projectName' | 'packageManager' | 'authMode'>,
): Promise<LocalEnvInput | null> {
  let resolved: LocalEnvInput | null = null;
  let cancelled = false;
  await settleStdinBeforePrompt();
  clearInkScreen();
  const instance = render(
    <LocalEnvPrompt
      frame={frame}
      onCancel={() => {
        cancelled = true;
      }}
      onSubmit={(value) => {
        resolved = value;
      }}
    />,
    { exitOnCtrlC: false },
  );
  await instance.waitUntilExit();
  if (cancelled) return null;
  return resolved;
}

export function renderInitStepFrame(state: InitStepFrameState): string {
  return renderToString(<InitStepFrame {...state} />);
}

export function renderInitCompleteFrame(input: {
  useExistingProject: boolean;
  projectName: string;
  nextCommand: string;
  bootstrapSession: boolean;
}): string {
  return renderToString(
    <Box flexDirection="column" paddingX={1}>
      <Box borderStyle="round" borderColor="green" paddingX={1} flexDirection="column">
        <Text color="greenBright" bold>
          Init Complete
        </Text>
        <Text color="whiteBright">
          {input.useExistingProject ? 'Project updated successfully.' : 'Project created successfully.'}
        </Text>
      </Box>
      <Box marginTop={1} flexDirection="column">
        <Text color="cyanBright" bold>
          Next steps
        </Text>
        {!input.useExistingProject ? <Text color="white">{`  cd ${input.projectName}`}</Text> : null}
        <Text color="white">{`  ${input.nextCommand}`}</Text>
      </Box>
      {!input.bootstrapSession ? (
        <Box marginTop={1} flexDirection="column">
          <Text color="yellowBright" bold>
            Local-only notes
          </Text>
          <Text color="gray">  1. Sign in locally with username `admin` and your temporary `SUPER_ADMIN_PASSWORD`.</Text>
          <Text color="gray">  2. Create a standard user account for yourself.</Text>
          <Text color="gray">  3. Remove `SUPER_ADMIN_PASSWORD` from `.dev.vars` once setup is complete.</Text>
        </Box>
      ) : null}
      <Box marginTop={1}>
        <Text color="gray">Docs: https://standardagents.ai/docs</Text>
      </Box>
    </Box>,
  );
}

export async function selectPackageManagerWithInk(
  detected: InitPackageManager,
  context: {
    projectName?: string;
    authMode?: InitAuthMode;
  } = {},
): Promise<InitPackageManager | null> {
  const options: SelectOption<InitPackageManager>[] = [
    { value: 'npm', label: 'npm', description: 'Default Node package manager.', badge: detected === 'npm' ? '(detected)' : undefined },
    { value: 'pnpm', label: 'pnpm', description: 'Fast, space-efficient monorepo manager.', badge: detected === 'pnpm' ? '(detected)' : undefined },
    { value: 'yarn', label: 'yarn', description: 'Classic workspace package manager.', badge: detected === 'yarn' ? '(detected)' : undefined },
    { value: 'bun', label: 'bun', description: 'Bun runtime package manager.', badge: detected === 'bun' ? '(detected)' : undefined },
  ];

  function PackageManagerPrompt({
    onCancel,
    onSubmit,
  }: {
    onCancel: () => void;
    onSubmit: (value: InitPackageManager) => void;
  }) {
    const { exit } = useApp();
    const [selectedIndex, setSelectedIndex] = useState(
      Math.max(0, options.findIndex((option) => option.value === detected)),
    );
    const selectedPm = options[selectedIndex]?.value ?? detected;

    useInput((input, key) => {
      if (key.upArrow || input === 'k') {
        setSelectedIndex((prev) => (prev - 1 + options.length) % options.length);
        return;
      }
      if (key.downArrow || input === 'j') {
        setSelectedIndex((prev) => (prev + 1) % options.length);
        return;
      }
      if (key.return) {
        onSubmit(selectedPm);
        exit();
        return;
      }
      if (key.escape) {
        onCancel();
        exit();
        return;
      }
      if (key.ctrl && input === 'c') {
        process.exit(1);
      }
    });

    return (
      <Box flexDirection="column">
        <InitStepFrame
          stage="project"
          title="Creating project scaffold"
          subtitle="Select package manager for install and scaffold commands."
          projectName={context.projectName}
          packageManager={selectedPm}
          authMode={context.authMode}
        />
        <Box marginTop={1} flexDirection="column" paddingX={1}>
          {options.map((option, index) => {
            const selected = index === selectedIndex;
            return (
              <Box key={option.value} marginBottom={0}>
                <Text color={selected ? 'black' : 'white'} backgroundColor={selected ? 'cyan' : undefined} bold={selected}>
                  {selected ? '›' : ' '} {option.label}
                  {option.badge ? ` ${option.badge}` : ''}
                </Text>
                {option.description ? <Text color={selected ? 'cyan' : 'gray'}>{`  ${option.description}`}</Text> : null}
              </Box>
            );
          })}
        </Box>
        <Box paddingX={1}>
          <Text color="gray">Use arrows (or j/k), Enter to continue, Esc to return.</Text>
        </Box>
      </Box>
    );
  }

  let resolved: InitPackageManager | null = null;
  let cancelled = false;
  await settleStdinBeforePrompt();
  clearInkScreen();
  const instance = render(
    <PackageManagerPrompt
      onCancel={() => {
        cancelled = true;
      }}
      onSubmit={(value) => {
        resolved = value;
      }}
    />,
    { exitOnCtrlC: false },
  );
  await instance.waitUntilExit();
  if (cancelled) return null;
  return resolved;
}

interface AuthModePromptProps {
  projectName?: string;
  packageManager?: string;
  onCancel: () => void;
  onSubmit: (value: InitAuthMode) => void;
}

function AuthModePrompt({ projectName, packageManager, onCancel, onSubmit }: AuthModePromptProps) {
  const { exit } = useApp();
  const options: SelectOption<InitAuthMode>[] = [
    {
      value: 'standardagents',
      label: 'StandardAgents',
      bullets: ['Free account', '$2 starting credit', 'Zero-config setup', 'Production deployments'],
    },
    {
      value: 'local',
      label: 'Local',
      bullets: ['Bring your own provider keys', 'Manual setup required', 'Local-only mode', 'Account required for production'],
    },
  ];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedMode = options[selectedIndex]?.value ?? 'standardagents';
  const columns = process.stdout.columns ?? 120;
  const horizontal = shouldUseSideBySideAuthCards(columns);
  const cardWidth = horizontal ? Math.max(34, Math.floor((columns - 8) / options.length)) : Math.max(42, columns - 6);

  useInput((input, key) => {
    if (key.upArrow || input === 'k' || key.leftArrow || input === 'h') {
      setSelectedIndex((prev) => (prev - 1 + options.length) % options.length);
      return;
    }
    if (key.downArrow || input === 'j' || key.rightArrow || input === 'l') {
      setSelectedIndex((prev) => (prev + 1) % options.length);
      return;
    }
    if (key.return) {
      onSubmit(selectedMode);
      exit();
      return;
    }
    if (key.escape) {
      onCancel();
      exit();
      return;
    }
    if (key.ctrl && input === 'c') {
      process.exit(1);
    }
  });

  return (
    <Box flexDirection="column">
      <InitStepFrame
        stage="auth"
        title="Account linking"
        subtitle="Choose how this project should be configured."
        projectName={projectName}
        packageManager={packageManager}
        authMode={selectedMode}
      />
      <Box marginTop={1} paddingX={1} flexDirection={horizontal ? 'row' : 'column'}>
        {options.map((option, index) => (
          <Box key={option.value} marginBottom={horizontal ? 0 : 1}>
            <OptionCard option={option} selected={index === selectedIndex} width={cardWidth} />
          </Box>
        ))}
      </Box>
      <Box paddingX={1}>
        <Text color="gray">Use Left/Right (or h/l), Enter to continue, Esc to return.</Text>
      </Box>
    </Box>
  );
}

export async function selectInitAuthModeWithInk(context: {
  projectName?: string;
  packageManager?: string;
} = {}): Promise<InitAuthMode | null> {
  let resolved: InitAuthMode | null = null;
  let cancelled = false;
  await settleStdinBeforePrompt();
  clearInkScreen();
  const instance = render(
    <AuthModePrompt
      projectName={context.projectName}
      packageManager={context.packageManager}
      onCancel={() => {
        cancelled = true;
      }}
      onSubmit={(value) => {
        resolved = value;
      }}
    />,
    { exitOnCtrlC: false },
  );
  await instance.waitUntilExit();
  if (cancelled) return null;
  return resolved;
}
